import React, { useState, useEffect, useCallback } from "react";
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, Tooltip, TextField, Chip, Dialog, DialogTitle, DialogContent, DialogActions, Button, Grid, Switch, FormControlLabel, Alert, CircularProgress, TablePagination } from "@mui/material";
import { Visibility as VisibilityIcon, Refresh as RefreshIcon, CheckCircle as CheckCircleIcon, Cancel as CancelIcon, PendingActions as PendingActionsIcon, InfoOutlined as InfoOutlinedIcon, ArrowBack as ArrowBackIcon } from "@mui/icons-material";
import { getData, postData, putData } from "../../../services/axiosInstance";
import { API_ENDPOINTS } from "../../../services/endpoints";
import Loader from "../../../common/spinner";
import { getUserData } from "../../../utils/auth";

export const getAbsoluteFileUrl = (fileData) => {
    if (!fileData) return "";
    const data = Array.isArray(fileData) ? fileData[0] : fileData;
    if (!data) return "";

    if (typeof data === "string") {
        return data.startsWith("http")
            ? data
            : `${API_ENDPOINTS.defaults.baseURL.replace(/\/api$/, "").replace(/\/$/, "")}${data.startsWith("/") ? data : `/${data}`}`;
    }

    let path = data.formUrl || data.form_url || data.url || data.filePath || data.file_url || data.proofUrl || data.form12A || data.file || data.path;

    if (!path) return "";
    if (path.startsWith("http")) return path;

    let baseUrl = API_ENDPOINTS.defaults.baseURL || "";
    let origin = baseUrl.replace(/\/api$/, "").replace(/\/$/, "");
    const normalizedPath = path.startsWith("/") ? path : `/${path}`;

    return `${origin}${normalizedPath}`;
};

function ITDeclarationsTab({ employees = [], onBack }) {
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [dialogOpen, setDialogOpen] = useState(false);
    const [isFromRunPayroll, setIsFromRunPayroll] = useState(false);

    useEffect(() => {
        const cameFromRunPayroll = localStorage.getItem("returnToRunPayrollFromITDeclarations");
        setIsFromRunPayroll(!!cameFromRunPayroll);
    }, []);

    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [loadingDetails, setLoadingDetails] = useState(false);
    const [taxSummary, setTaxSummary] = useState(null);
    const [proofs, setProofs] = useState([]);
    const [actionLoading, setActionLoading] = useState(false);
    const [taxSummaries, setTaxSummaries] = useState([]);
    const [page, setPage] = useState(0);
    const [verifyDialogOpen, setVerifyDialogOpen] = useState(false);
    const [selectedProofForVerify, setSelectedProofForVerify] = useState(null);
    const [verifyAction, setVerifyAction] = useState("");
    const [verifyRemarks, setVerifyRemarks] = useState("");
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [form12BData, setForm12BData] = useState(null);
    const [isForm12BLoading, setIsForm12BLoading] = useState(false);
    const [showRegimeUpdateSection, setShowRegimeUpdateSection] = useState(false);
    const [newRegimeSelection, setNewRegimeSelection] = useState("");
    const [isRegimeUpdating, setIsRegimeUpdating] = useState(false);
    const [regimeRejectionReason, setRegimeRejectionReason] = useState("");
    const [onlyPendingRequests, setOnlyPendingRequests] = useState(false);
    const [onlyPendingProofs, setOnlyPendingProofs] = useState(false);

    const getFinancialYearStart = () => {
        const today = new Date();
        const month = today.getMonth();
        const year = today.getFullYear();
        return month < 3 ? (year - 1).toString() : year.toString();
    };
    const currentFinancialYear = getFinancialYearStart();

    const getSubmissionWindow = (deadlineStr) => {
        const today = new Date();

        let fyEndDate;
        if (deadlineStr) {
            const [monthStr, dayStr] = deadlineStr.split("-");
            const month = parseInt(monthStr) - 1;
            const day = parseInt(dayStr);
            const endYear = month <= 3 ? parseInt(currentFinancialYear) + 1 : parseInt(currentFinancialYear);
            fyEndDate = new Date(endYear, month, day, 23, 59, 59);
        } else {
            const fyEndYear = parseInt(currentFinancialYear) + 1;
            fyEndDate = new Date(fyEndYear, 2, 31, 23, 59, 59);
        }
        const isOpen = today <= fyEndDate;
        const diffTime = fyEndDate.getTime() - today.getTime();
        const daysRemaining = Math.max(0, Math.ceil(diffTime / (1000 * 60 * 60 * 24)));
        const isWarningPeriod = isOpen && daysRemaining <= 30;
        return { isOpen, isWarningPeriod, daysRemaining };
    };

    const { isOpen: isSubmissionOpen, isWarningPeriod: isSubmissionWarning, daysRemaining } = getSubmissionWindow(taxSummaries[0]?.submissionDeadline);

    const isPendingProofStatus = (status) =>
        ["PENDING", "SUBMITTED"].includes(String(status || "").toUpperCase());

    const fetchBulkTaxSummary = useCallback(async () => {
        setLoading(true); // START LOADER
        try {
            const response = await getData(API_ENDPOINTS.getTaxSummaryByFinancialYear(null, currentFinancialYear));
            if (response && response.status === "SUCCESS") {
                setTaxSummaries(response.result || []);
            }
        } catch (err) {
            return null;
        } finally {
            setLoading(false); // STOP LOADER
        }
    }, [currentFinancialYear]);

    useEffect(() => {
        fetchBulkTaxSummary();
    }, [fetchBulkTaxSummary]);

    const filteredIT = taxSummaries.filter(
        (emp) => {
            const matchesSearch = (emp.employeeName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                emp.employeeCode?.toLowerCase().includes(searchQuery.toLowerCase()));

            const matchesFilter = onlyPendingRequests ? emp.regimeChangeRequestSent : true;
            const matchesProofFilter = onlyPendingProofs
                ? isPendingProofStatus(emp.proofStatus)
                : true;

            const isRelevant = (onlyPendingRequests || onlyPendingProofs)
                ? true
                : (emp.annualTax > 0 || emp.totalDeclaredAmount > 0 || emp.proofStatus);

            return isRelevant && matchesSearch && matchesFilter && matchesProofFilter;
        }
    );

    const pendingRequestsCount = taxSummaries.filter(emp => emp.regimeChangeRequestSent).length;
    const pendingProofsCount = taxSummaries.filter(emp => isPendingProofStatus(emp.proofStatus)).length;

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleDialogClose = () => {
        setDialogOpen(false);
        setSelectedEmployee(null);
        setTaxSummary(null);
        setForm12BData(null);
    };

    const openVerifyDialog = (proof, action) => {
        setSelectedProofForVerify(proof);
        setVerifyAction(action.toUpperCase());
        setVerifyRemarks(proof.remarks || proof.comments || "");
        setVerifyDialogOpen(true);
    };

    const closeVerifyDialog = () => {
        setVerifyDialogOpen(false);
        setSelectedProofForVerify(null);
        setVerifyAction("");
        setVerifyRemarks("");
    };

    const fetchEmployeeTaxDetails = async (employee) => {
        setLoadingDetails(true);
        setSelectedEmployee(employee);
        const idToFetch = employee.employeePersonalId || employee.id || employee.employeeId;

        const response = await getData(API_ENDPOINTS.getTaxSummaryByFinancialYear(idToFetch, currentFinancialYear));
        const proofsResponse = await getData(API_ENDPOINTS.getEmployeeTaxDeclaration(idToFetch, currentFinancialYear));

        if (response.status === "SUCCESS") {
            setTaxSummary(response.result);
        } else {
            setTaxSummary({ declarations: [], regime: employee.oldRegime ? "Old" : "New", status: employee.status });
        }

        if (proofsResponse && (proofsResponse.status === "SUCCESS" || proofsResponse.result)) {
            setProofs(proofsResponse.result || []);
        } else {
            setProofs([]);
        }

        fetchForm12B(idToFetch);
        setLoadingDetails(false);
        setDialogOpen(true);
    };

    const submitVerification = async () => {
        if (verifyAction === "REJECTED" && !verifyRemarks.trim()) {
            alert("Remarks are required when rejecting a proof.");
            return;
        }

        setActionLoading(true);
        const payload = {
            verifiedBy: getUserData().id,
            id: selectedProofForVerify.id,
            status: verifyAction,
            remarks: verifyRemarks.trim() || (verifyAction === "APPROVED" ? "Verified and approved" : "Rejected")
        };

        const response = await postData(API_ENDPOINTS.verifyIncomeTaxProof, payload);

        if (response && (response.status === "SUCCESS" || response.success)) {
            closeVerifyDialog();
            await fetchEmployeeTaxDetails(selectedEmployee);
        } else {
            alert("Failed to verify proof");
        }
        setActionLoading(false);
    };

    const handleRegimeUpdate = async (action = 'MANUAL') => {
        const isApproval = action === 'APPROVE';
        const isRejection = action === 'REJECT';

        if (action === 'MANUAL' && !newRegimeSelection) return;
        if (isRejection && !regimeRejectionReason.trim()) {
            alert("Please provide a reason for rejection");
            return;
        }

        setIsRegimeUpdating(true);
        try {
            const payload = {
                employeePersonalId: selectedEmployee.employeePersonalId || selectedEmployee.id,
                taxRegime: isApproval ? taxSummary?.requestedRegime : (action === 'MANUAL' ? newRegimeSelection : null),
                regimeChangeApproved: isApproval,
                regimeChangeRejected: isRejection,
                regimeChangeRejectionReason: isRejection ? regimeRejectionReason : null
            };

            const response = await putData(API_ENDPOINTS.updatePayrollSettings, payload);
            if (response && (response.status === "SUCCESS")) {
                await fetchEmployeeTaxDetails(selectedEmployee);
                await fetchBulkTaxSummary();
                setShowRegimeUpdateSection(false);
                setRegimeRejectionReason("");
                alert(`Tax regime successfully updated`);
            } else {
                alert(response.message || "Failed to update tax regime");
            }
        } catch (error) {
            setIsRegimeUpdating(false);
        } finally {
            setIsRegimeUpdating(false);
        }
    };

    const fetchForm12B = async (employeePersonalId) => {
        setIsForm12BLoading(true);
        try {
            const response = await getData(API_ENDPOINTS.getIncomeTaxForm(employeePersonalId, currentFinancialYear));
            if (response && (response.status === "SUCCESS" || response.success)) {
                setForm12BData(response.result);
            }
        } catch (error) {
        } finally {
            setIsForm12BLoading(false);
        }
    };

    useEffect(() => {
        const savedSearch = localStorage.getItem("itDeclarationSearchQuery");
        if (savedSearch) {
            setSearchQuery(savedSearch);
            // Clean up so it doesn't stay there forever
            localStorage.removeItem("itDeclarationSearchQuery");
        }

        const cameFromRunPayroll = localStorage.getItem("returnToRunPayrollFromITDeclarations");
        setIsFromRunPayroll(!!cameFromRunPayroll);
    }, []);

    if (loading) return <Loader />;

    return (
        <Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    {isFromRunPayroll && onBack && (
                        <Tooltip title="Back to Payroll Process">
                            <IconButton onClick={onBack} size="small" sx={{ color: "#1565c0" }}>
                                <ArrowBackIcon />
                            </IconButton>
                        </Tooltip>
                    )}
                    <Box>
                        <Typography variant="h6" fontWeight="bold" color="primary">
                            IT Declarations & Proofs
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Manage tax regimes, declarations, and proof verification.
                        </Typography>
                    </Box>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
                    <Paper elevation={0} sx={{ p: 1, border: "1px solid #e0e0e0", backgroundColor: "#e3f2fd", mr: 2 }}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={onlyPendingProofs}
                                    onChange={(e) => {
                                        setOnlyPendingProofs(e.target.checked);
                                        setPage(0);
                                    }}
                                    name="onlyPendingProofs"
                                    color="info"
                                />
                            }
                            label={
                                <Box>
                                    <Typography variant="body2" fontWeight="bold">Pending Proofs</Typography>
                                    <Typography variant="caption" color="info.main" fontWeight="bold">
                                        {pendingProofsCount} {pendingProofsCount === 1 ? 'employee' : 'employees'} found
                                    </Typography>
                                </Box>
                            }
                        />
                    </Paper>

                    <Paper elevation={0} sx={{ p: 1, border: "1px solid #e0e0e0", backgroundColor: "#fff9f0" }}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={onlyPendingRequests}
                                    onChange={(e) => {
                                        setOnlyPendingRequests(e.target.checked);
                                        setPage(0);
                                    }}
                                    name="onlyPending"
                                    color="warning"
                                />
                            }
                            label={
                                <Box>
                                    <Typography variant="body2" fontWeight="bold">Pending Regime Requests</Typography>
                                    <Typography variant="caption" color="warning.main" fontWeight="bold">
                                        {pendingRequestsCount} {pendingRequestsCount === 1 ? 'request' : 'requests'} found
                                    </Typography>
                                </Box>
                            }
                        />
                    </Paper>
                </Box>
            </Box>

            <Grid container spacing={2} mb={3} alignItems="center">
                <Grid size={{ xs: 12, sm: 8, md: 10 }}>
                    <TextField
                        fullWidth
                        label="Search Employee"
                        size="small"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 4, md: 2 }}>
                    <Tooltip title="Refresh Table">
                        <Button fullWidth startIcon={<RefreshIcon />} onClick={fetchBulkTaxSummary} sx={{ textTransform: 'none', color: 'text.secondary' }}>Refresh</Button>
                    </Tooltip>
                </Grid>
            </Grid>

            <TableContainer component={Paper} sx={{ borderRadius: '12px', border: '1px solid #E2E8F0', boxShadow: 'none' }}>
                <Table sx={{ minWidth: 900 }}>
                    <TableHead sx={{ bgcolor: '#EFF6FF' }}>
                        <TableRow>
                            <TableCell sx={{ color: '#1D4ED8', fontWeight: 700 }}>Employee</TableCell>
                            <TableCell sx={{ color: '#1D4ED8', fontWeight: 700 }}>Department</TableCell>
                            <TableCell sx={{ color: '#1D4ED8', fontWeight: 700 }} align="right">Gross Annual (₹)</TableCell>
                            <TableCell sx={{ color: '#1D4ED8', fontWeight: 700 }} align="right">Taxable Income (₹)</TableCell>
                            <TableCell sx={{ color: '#1D4ED8', fontWeight: 700 }} align="right">Annual Tax (₹)</TableCell>
                            <TableCell sx={{ color: '#1D4ED8', fontWeight: 700 }} align="right">Surcharge (₹)</TableCell>
                            <TableCell sx={{ color: '#1D4ED8', fontWeight: 700 }} align="right">Monthly TDS (₹)</TableCell>
                            <TableCell sx={{ color: '#1D4ED8', fontWeight: 700 }} align="center">Regime</TableCell>
                            <TableCell sx={{ color: '#1D4ED8', fontWeight: 700 }} align="center">Action</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody sx={{ bgcolor: '#FFFFFF' }}>
                        {filteredIT.length > 0 ? (
                            filteredIT
                                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                .map((emp) => (
                                    <TableRow key={emp.employeePersonalId || emp.employeeCode} hover sx={{
                                        '&:hover': {
                                            bgcolor: '#F1F5F9 !important'
                                        }
                                    }}>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight={500}>{emp.employeeName}</Typography>
                                            <Typography variant="caption" color="text.secondary">{emp.employeeCode}</Typography>
                                        </TableCell>
                                        <TableCell>{emp.departmentName || "N/A"}</TableCell>
                                        <TableCell align="right">₹{emp.grossAnnual?.toLocaleString() || 0}</TableCell>
                                        <TableCell align="right">₹{emp.taxableIncome?.toLocaleString() || 0}</TableCell>
                                        <TableCell align="right">₹{emp.annualTax?.toLocaleString() || 0}</TableCell>
                                        <TableCell align="right">₹{emp.surcharge?.toLocaleString() || 0}</TableCell>
                                        <TableCell align="right">₹{emp.monthlyTDS?.toLocaleString() || 0}</TableCell>
                                        <TableCell align="center">
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                                                <Chip
                                                    label={emp.regime || "Not Selected"}
                                                    color={emp.regime === "Old Regime" ? "secondary" : "primary"}
                                                    variant="outlined"
                                                    size="small"
                                                />
                                                {emp.regimeChangeRequestSent && (
                                                    <Tooltip title="Regime Change Requested">
                                                        <IconButton size="small" sx={{ p: 0.5, color: '#f57c00' }}>
                                                            <PendingActionsIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </Box>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Tooltip title="View Declaration">
                                                <IconButton size="small" color="primary" onClick={() => fetchEmployeeTaxDetails(emp)}>
                                                    <VisibilityIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                                    No employees found
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={filteredIT.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
            />

            <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="md" fullWidth>
                <DialogTitle sx={{ fontWeight: 600, color: "#1565c0", borderBottom: "1px solid #e0e0e0", display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    IT Declaration Review
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip
                            label={isSubmissionOpen ? "Submission Window: OPEN" : "Submission Window: CLOSED"}
                            color={isSubmissionOpen ? "success" : "error"}
                            variant="filled"
                            sx={{ fontWeight: 'bold' }}
                        />
                        {isSubmissionWarning && isSubmissionOpen && (
                            <Chip
                                label={`${daysRemaining} Days Until Deadline`}
                                color="warning"
                                variant="outlined"
                                sx={{ ml: 1, fontWeight: 'bold' }}
                            />
                        )}
                    </Box>
                </DialogTitle>
                <DialogContent dividers sx={{ p: 3 }}>
                    {loadingDetails ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <>
                            {selectedEmployee && (
                                <Box sx={{ mb: 3, p: 2, backgroundColor: "#f5f7fa", borderRadius: 1 }}>
                                    <Grid container spacing={2}>
                                        <Grid size={{ xs: 6 }}>
                                            <Typography variant="body2" color="text.secondary">Employee</Typography>
                                            <Typography variant="body1" fontWeight={500}>{selectedEmployee.employeeName} ({selectedEmployee.employeeCode})</Typography>
                                        </Grid>
                                        <Grid size={{ xs: 6 }}>
                                            <Typography variant="body2" color="text.secondary">Tax Regime</Typography>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Typography variant="body1" fontWeight={500}>
                                                    {taxSummary?.regime || "Old Regime"}
                                                </Typography>
                                                {taxSummary?.regimeChangeRequestSent && (
                                                    <Tooltip title="Employee has requested to change their tax regime">
                                                        <Box sx={{ display: 'flex', alignItems: 'center', color: 'warning.main' }}>
                                                            <InfoOutlinedIcon sx={{ fontSize: 16, mr: 0.5 }} />
                                                            <Typography variant="caption" fontWeight="bold">Change Requested</Typography>
                                                        </Box>
                                                    </Tooltip>
                                                )}
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    sx={{ ml: 1, textTransform: 'none', height: 28 }}
                                                    onClick={() => {
                                                        setNewRegimeSelection(taxSummary?.regime === "Old Regime" ? "New Regime" : "Old Regime");
                                                        setShowRegimeUpdateSection(!showRegimeUpdateSection);
                                                    }}
                                                >
                                                    {showRegimeUpdateSection ? "Cancel" : "Change Regime"}
                                                </Button>
                                            </Box>
                                        </Grid>
                                        {taxSummary?.regimeChangeRequestSent && (
                                            <Grid size={{ xs: 12 }}>
                                                <Paper variant="outlined" sx={{ p: 2, mb: 1, bgcolor: '#fff3e0', border: '1px solid #ff9800' }}>
                                                    <Typography variant="subtitle2" color="warning.dark" fontWeight="bold" gutterBottom>
                                                        Pending Regime Change Request
                                                    </Typography>
                                                    <Box sx={{ mb: 2 }}>
                                                        <Typography variant="body2">
                                                            <b>Requested Regime:</b> {taxSummary.requestedRegime}
                                                        </Typography>
                                                        <Typography variant="body2">
                                                            <b>Reason:</b> {taxSummary.regimeChangeReason || "No reason provided"}
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                                        <Button
                                                            variant="contained"
                                                            color="success"
                                                            size="small"
                                                            disabled={isRegimeUpdating}
                                                            onClick={() => handleRegimeUpdate('APPROVE')}
                                                        >
                                                            Approve Request
                                                        </Button>
                                                        <Box sx={{ display: 'flex', gap: 1, flexGrow: 1 }}>
                                                            <TextField
                                                                size="small"
                                                                placeholder="Rejection reason..."
                                                                value={regimeRejectionReason}
                                                                onChange={(e) => setRegimeRejectionReason(e.target.value)}
                                                                sx={{ flexGrow: 1, bgcolor: 'white' }}
                                                            />
                                                            <Button
                                                                variant="contained"
                                                                color="error"
                                                                size="small"
                                                                disabled={isRegimeUpdating}
                                                                onClick={() => handleRegimeUpdate('REJECT')}
                                                            >
                                                                Reject
                                                            </Button>
                                                        </Box>
                                                    </Box>
                                                </Paper>
                                            </Grid>
                                        )}
                                        {showRegimeUpdateSection && (
                                            <Grid size={{ xs: 12 }}>
                                                <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f1f8e9', border: '1px solid #4caf50' }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                            <Typography variant="body2" fontWeight="bold">Manual Update to:</Typography>
                                                            <Chip
                                                                label={newRegimeSelection}
                                                                color={newRegimeSelection === "Old Regime" ? "secondary" : "primary"}
                                                                size="small"
                                                            />
                                                            <Typography variant="caption" color="text.secondary">
                                                                (Direct override - ignores any pending requests)
                                                            </Typography>
                                                        </Box>
                                                        <Button
                                                            variant="contained"
                                                            color="success"
                                                            size="small"
                                                            disabled={isRegimeUpdating}
                                                            onClick={() => handleRegimeUpdate('MANUAL')}
                                                        >
                                                            {isRegimeUpdating ? "Updating..." : "Save Change"}
                                                        </Button>
                                                    </Box>
                                                </Paper>
                                            </Grid>
                                        )}
                                        <Grid size={{ xs: 6 }}>
                                            <Typography variant="body2" color="text.secondary">PAN</Typography>
                                            <Typography variant="body1" fontWeight={500}>{selectedEmployee.panNumber || "N/A"}</Typography>
                                        </Grid>
                                        <Grid size={{ xs: 6 }}>
                                            <Typography variant="body2" color="text.secondary">Taxable Income (Est.)</Typography>
                                            <Typography variant="body1" fontWeight={500}>₹{taxSummary?.taxableIncome?.toLocaleString() || "0"}</Typography>
                                        </Grid>
                                        <Grid size={{ xs: 3 }}>
                                            <Typography variant="body2" color="text.secondary">Surcharge</Typography>
                                            <Typography variant="body1" fontWeight={500}>₹{taxSummary?.surcharge?.toLocaleString() || "0"}</Typography>
                                        </Grid>
                                        <Grid size={{ xs: 3 }}>
                                            <Typography variant="body2" color="text.secondary">Marginal Relief</Typography>
                                            <Typography variant="body1" fontWeight={500} color="green">-₹{taxSummary?.marginalRelief?.toLocaleString() || "0"}</Typography>
                                        </Grid>
                                    </Grid>
                                </Box>
                            )}

                            <Typography variant="h6" sx={{ mb: 1, mt: 1, fontWeight: 600 }}>
                                Form 12B Details
                            </Typography>
                            <Paper variant="outlined" sx={{ p: 2, backgroundColor: "#f5f7fa", mb: 4, border: '1px solid #1565c0' }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="body2" fontWeight={500}>Previous Employer Income Proof (Form 12B)</Typography>
                                    {isForm12BLoading ? (
                                        <CircularProgress size={20} />
                                    ) : getAbsoluteFileUrl(form12BData) ? (
                                        <Button
                                            variant="contained"
                                            size="small"
                                            startIcon={<VisibilityIcon />}
                                            onClick={() => window.open(getAbsoluteFileUrl(form12BData), "_blank")}
                                        >
                                            View Form 12B
                                        </Button>
                                    ) : (
                                        <Typography variant="caption" color="text.secondary">Not Uploaded</Typography>
                                    )}
                                </Box>
                            </Paper>

                            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                                {isSubmissionOpen ? "Investment & Proof Verification" : "Declared Investments (FY Ended)"}
                            </Typography>

                            {taxSummary?.regime === "New Regime" ? (
                                <Alert severity="success" sx={{ mb: 2 }}>
                                    Employee has opted for New Tax Regime. No investment proofs required.
                                </Alert>
                            ) : (
                                <Alert severity="info" sx={{ mb: 2 }}>
                                    {isSubmissionOpen
                                        ? "Current Status: Submission Window is Open. Employees can upload proofs anytime during the financial year."
                                        : "Current Status: Submission Window is Closed. Viewing archived data for this financial year."}
                                </Alert>
                            )}

                            {taxSummary?.regime !== "New Regime" && (
                                <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid #e0e0e0", mb: 3 }}>
                                    <Table size="small">
                                        <TableHead sx={{ backgroundColor: "#f5f7fa" }}>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 600 }}>Section</TableCell>
                                                <TableCell sx={{ fontWeight: 600 }} align="right">Amount Declared (₹)</TableCell>
                                                <TableCell sx={{ fontWeight: 600 }} align="center">Status</TableCell>
                                                <TableCell sx={{ fontWeight: 600 }}>Documents</TableCell>
                                                <TableCell sx={{ fontWeight: 600 }} align="center">Actions</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {proofs.length > 0 ? (
                                                proofs.map((proof) => (
                                                    <TableRow key={proof.id} hover>
                                                        <TableCell sx={{ fontWeight: 500 }}>{proof.section || "Unknown Section"}</TableCell>
                                                        <TableCell align="right">₹{(proof.declaredAmount || proof.amount || 0).toLocaleString()}</TableCell>
                                                        <TableCell align="center">
                                                            <Chip
                                                                label={proof.status || "Pending"}
                                                                color={proof.status?.toUpperCase() === "APPROVED" ? "success" : proof.status?.toUpperCase() === "REJECTED" ? "error" : "warning"}
                                                                size="small"
                                                                variant="outlined"
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            {proof.proofUrl ? (
                                                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                                                    <Typography
                                                                        variant="body2"
                                                                        component="a"
                                                                        href={proof.proofUrl}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        sx={{ textDecoration: 'underline', cursor: 'pointer', color: 'blue' }}
                                                                    >
                                                                        View Document
                                                                    </Typography>
                                                                </Box>
                                                            ) : (
                                                                <Typography variant="caption" color="text.secondary">No Document</Typography>
                                                            )}
                                                        </TableCell>
                                                        <TableCell align="center">
                                                            {(() => {
                                                                const status = proof.proofStatus || proof.status;
                                                                const statusUpper = status?.toUpperCase() || "";
                                                                const isApproved = statusUpper === "APPROVED";
                                                                const isRejected = statusUpper === "REJECTED";

                                                                if (isApproved || isRejected) {
                                                                    return (
                                                                        <Tooltip title={isApproved ? "Approved" : "Rejected"}>
                                                                            <Box sx={{ color: isApproved ? "success.main" : "error.main", display: 'flex', justifyContent: 'center' }}>
                                                                                {isApproved ? <CheckCircleIcon /> : <CancelIcon />}
                                                                            </Box>
                                                                        </Tooltip>
                                                                    );
                                                                } else {
                                                                    return (
                                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'center' }}>
                                                                            <Tooltip title="Approve">
                                                                                <IconButton
                                                                                    size="small"
                                                                                    color="success"
                                                                                    onClick={() => openVerifyDialog(proof, "APPROVED")}
                                                                                >
                                                                                    <CheckCircleIcon />
                                                                                </IconButton>
                                                                            </Tooltip>
                                                                            <Tooltip title="Reject">
                                                                                <IconButton
                                                                                    size="small"
                                                                                    color="error"
                                                                                    onClick={() => openVerifyDialog(proof, "REJECTED")}
                                                                                >
                                                                                    <CancelIcon />
                                                                                </IconButton>
                                                                            </Tooltip>
                                                                        </Box>
                                                                    );
                                                                }
                                                            })()}
                                                        </TableCell>
                                                    </TableRow>
                                                ))
                                            ) : (
                                                <TableRow>
                                                    <TableCell colSpan={5} align="center">No proofs found for this employee.</TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2, borderTop: "1px solid #e0e0e0" }}>
                    <Button onClick={handleDialogClose} variant="outlined">Close</Button>
                    {isFromRunPayroll && onBack && (
                        <Button
                            onClick={onBack}
                            variant="contained"
                            color="primary"
                            startIcon={<ArrowBackIcon />}
                        >
                            Back to Payroll
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            <Dialog open={verifyDialogOpen} onClose={closeVerifyDialog} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: 600, color: verifyAction === "APPROVED" ? "#2e7d32" : "#d32f2f", borderBottom: "1px solid #e0e0e0" }}>
                    {verifyAction === "APPROVED" ? "Approve Proof" : "Reject Proof"}
                </DialogTitle>
                <DialogContent dividers sx={{ p: 3 }}>
                    {selectedProofForVerify && (
                        <>
                            <Box sx={{ mb: 3, p: 2, backgroundColor: "#f5f7fa", borderRadius: 1 }}>
                                <Grid container spacing={2}>
                                    <Grid size={{ xs: 12 }}>
                                        <Typography variant="body2" color="text.secondary">Section</Typography>
                                        <Typography variant="body1" fontWeight={500}>{selectedProofForVerify.section || "Unknown Section"}</Typography>
                                    </Grid>
                                    <Grid size={{ xs: 12 }}>
                                        <Typography variant="body2" color="text.secondary">Declared Amount</Typography>
                                        <Typography variant="body1" fontWeight={500}>₹{selectedProofForVerify.amount?.toLocaleString() || 0}</Typography>
                                    </Grid>
                                    <Grid size={{ xs: 12 }}>
                                        <Typography variant="body2" color="text.secondary">Action</Typography>
                                        <Chip
                                            label={verifyAction === "APPROVED" ? "Approved" : "Rejected"}
                                            color={verifyAction === "APPROVED" ? "success" : "error"}
                                            size="small"
                                        />
                                    </Grid>
                                </Grid>
                            </Box>

                            <TextField
                                label={verifyAction === "REJECTED" ? "Remarks (Required)" : "Remarks (Optional)"}
                                multiline
                                rows={4}
                                fullWidth
                                value={verifyRemarks}
                                onChange={(e) => setVerifyRemarks(e.target.value)}
                                placeholder={verifyAction === "APPROVED" ? "Enter approval remarks (optional)" : "Enter reason for rejection (required)"}
                                required={verifyAction === "REJECTED"}
                                error={verifyAction === "REJECTED" && !verifyRemarks.trim()}
                                helperText={verifyAction === "REJECTED" && !verifyRemarks.trim() ? "Remarks are required for rejection" : ""}
                            />
                        </>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2, borderTop: "1px solid #e0e0e0" }}>
                    <Button onClick={closeVerifyDialog} variant="outlined" disabled={actionLoading}>Cancel</Button>
                    <Button
                        onClick={submitVerification}
                        variant="contained"
                        color={verifyAction === "APPROVED" ? "success" : "error"}
                        disabled={actionLoading}
                    >
                        {actionLoading ? <CircularProgress size={20} /> : `${verifyAction === "APPROVED" ? "Approve" : "Reject"} Proof`}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ITDeclarationsTab;